/* eslint-disable simple-import-sort/imports */
import React from 'react';
import { styled } from 'styled-components';

import { Reveal, Stagger, StaggerItem } from 'pages/HomeV2/components/Motion';
import { Container, Eyebrow, Lead, Title, v2 } from 'pages/HomeV2/v2';

import { goodieBenefits } from '../data-goodie';
import { CheckIcon } from '../icons';

/* Existing product screenshot (tooth selection + anaesthesia options),
   also used in the feature section of the start page. */
const SCREENSHOT = '/info-faqs/img-1.webp';

const Wrapper = styled.section`
  position: relative;
  background: ${v2.bg};
  border-top: 1px solid ${v2.border};
  padding: clamp(72px, 9vw, 130px) 0;
  overflow: hidden;
`;

const Grid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 44px;
  align-items: center;

  @media (min-width: 1024px) {
    grid-template-columns: minmax(0, 0.95fr) minmax(0, 1.05fr);
    gap: clamp(40px, 6vw, 96px);
  }
`;

const Copy = styled.div`
  display: flex;
  flex-direction: column;
  gap: 22px;

  ${Title} {
    font-size: clamp(1.85rem, 3.9vw, 3rem);
  }
`;

const Benefits = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin: 8px 0 0;
  padding: 0;
  list-style: none;
`;

const Benefit = styled(StaggerItem)`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px 16px;
  border-radius: 16px;
  border: 1px solid ${v2.border};
  background: ${v2.surface};
  font-family: ${v2.fontBody};
  font-size: 15.5px;
  font-weight: 600;
  color: ${v2.text};
  line-height: 1.4;

  span.check {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 30px;
    height: 30px;
    border-radius: 50%;
    background: rgba(52, 211, 153, 0.14);
    border: 1px solid rgba(52, 211, 153, 0.4);
    color: ${v2.green};

    svg {
      width: 15px;
      height: 15px;
    }
  }
`;

const Stage = styled.div`
  position: relative;

  @media (min-width: 1024px) {
    width: 108%;
  }
`;

const StageGlow = styled.div`
  position: absolute;
  inset: -12%;
  background: radial-gradient(
      ellipse 55% 60% at 30% 30%,
      rgba(30, 150, 252, 0.3),
      transparent 70%
    ),
    radial-gradient(
      ellipse 50% 55% at 80% 80%,
      rgba(255, 159, 79, 0.2),
      transparent 70%
    );
  filter: blur(40px);
  z-index: 0;
  pointer-events: none;
`;

/* Same framing as the app window in the start page hero. */
const Shot = styled.figure`
  position: relative;
  z-index: 1;
  margin: 0;
  border-radius: 18px;
  overflow: hidden;
  border: 1px solid ${v2.borderStrong};
  background: ${v2.bgElevated};
  box-shadow:
    0 30px 70px rgba(0, 0, 0, 0.45),
    0 0 0 1px rgba(255, 255, 255, 0.04) inset;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: inherit;
    padding: 1px;
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.28),
      transparent 40%
    );
    -webkit-mask:
      linear-gradient(#fff 0 0) content-box,
      linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    pointer-events: none;
    z-index: 2;
  }

  img {
    display: block;
    width: 100%;
    height: auto;
  }

  figcaption {
    position: absolute;
    left: 14px;
    bottom: 14px;
    z-index: 3;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 8px 12px;
    border-radius: 100px;
    border: 1px solid ${v2.borderStrong};
    background: rgba(14, 24, 40, 0.86);
    backdrop-filter: blur(12px);
    font-family: ${v2.fontBody};
    font-size: 12.5px;
    font-weight: 600;
    color: ${v2.text};

    &::before {
      content: '';
      width: 7px;
      height: 7px;
      border-radius: 50%;
      background: ${v2.orange};
      box-shadow: 0 0 10px ${v2.orange};
    }
  }
`;

const GoodieUsp = () => (
  <Wrapper aria-labelledby="goodie-usp-title">
    <Container>
      <Grid>
        <Copy>
          <Reveal>
            <Eyebrow $accent="orange">Leistungserfassung</Eyebrow>
          </Reveal>
          <Reveal delay={0.1}>
            <Title id="goodie-usp-title">
              Dokumentieren und abrechnen –{' '}
              <span className="grad">ohne unnötige Klicks</span>.
            </Title>
          </Reveal>
          <Reveal delay={0.2}>
            <Lead>
              FOXI wurde speziell für die Abläufe deutscher Zahnarztpraxen
              entwickelt. Statt allgemeiner Praxissoftware steht die schnelle
              und intelligente Leistungserfassung im Mittelpunkt.
            </Lead>
          </Reveal>
          <Stagger delay={0.25} gap={0.1}>
            <Benefits>
              {goodieBenefits.map((benefit) => (
                <li key={benefit}>
                  <Benefit y={18}>
                    <span className="check">
                      <CheckIcon />
                    </span>
                    {benefit}
                  </Benefit>
                </li>
              ))}
            </Benefits>
          </Stagger>
        </Copy>

        <Reveal delay={0.15} y={40}>
          <Stage>
            <StageGlow aria-hidden />
            <Shot>
              <img
                src={SCREENSHOT}
                alt="FOXI Leistungserfassung: Zahnauswahl mit Flächen und Anästhesie-Optionen"
                width="795"
                height="720"
                loading="lazy"
                decoding="async"
              />
              <figcaption>Echte Ansicht aus FOXI</figcaption>
            </Shot>
          </Stage>
        </Reveal>
      </Grid>
    </Container>
  </Wrapper>
);

export default GoodieUsp;
